import { IHeaderBidderListInfo } from '~/src/dash/api/headerbidders/headerbidders_models';
import { parseArray, transformModel } from '~/src/dash/api/resource-transforms';
import { createUrl, restPost } from '~/src/dash/api/rest';
import { onlyNumeric } from '~/src/dash/globals/constants';

interface IHeaderBidderQueries {
  [key: string]: any;
}

export const transformReqBody = transformModel<any>({
  selectedChannelIds: parseArray(['selectedChannelIds'], (obj: any) => {
    return obj;
  }),
  preloads: parseArray(['preloads'], (obj: any) => {
    return obj;
  }),
  uiFields: parseArray(['uiFields'], (obj: any) => {
    return obj;
  }),
});

type simplifiedHeaderBiddersPreload =
  | 'ChannelIDS'
  | 'ChannelGroupIDS'
  | 'CampaignIDS'
  | 'AudienceDeals'
  | 'ABTests'
  | 'HeaderBidderPastWeekSummary';

// choose from the preloads below to put in a preloads array when you need certain preloads
export const simplifiedHeaderBidderPreloads = {
  SITE_IDS: 'SiteIDS' as simplifiedHeaderBiddersPreload,
  CHANNEL_GROUP_IDS: 'ChannelGroupIDS' as simplifiedHeaderBiddersPreload,
  CAMPAIGN_IDS: 'CampaignIDS' as simplifiedHeaderBiddersPreload,
  AUDIENCE_DEALS: 'AudienceDeals' as simplifiedHeaderBiddersPreload,
  AB_TESTS: 'ABTests' as simplifiedHeaderBiddersPreload,
  HEADER_BIDDER_PAST_WEEK_SUMMARY: 'HeaderBidderPastWeekSummary' as simplifiedHeaderBiddersPreload,
  BIDDER_LABELS: 'BidderLabels' as simplifiedHeaderBiddersPreload,
};

type simplifiedHeaderBiddersUiField =
  | 'id'
  | 'created_at'
  | 'updated_at'
  | 'deleted_at'
  | 'name'
  | 'bidder'
  | 'active'
  | 'super_only'
  | 'params'
  | 'priority'
  | 'ui_bidder_types'
  | 'flexible_bidding_enabled'
  | 'flexible_bidding_thresholds'
  | 'enable_spotx_bidder_association'
  | 'enable_magnite_ctv_bidder_association';

// choose from the ui fields below to put in a uiFields array when you need certain ui fields
export const simplifiedHeaderBidderUiFields = {
  ID: 'id' as simplifiedHeaderBiddersUiField,
  CREATED_AT: 'created_at' as simplifiedHeaderBiddersUiField,
  UPDATED_AT: 'updated_at' as simplifiedHeaderBiddersUiField,
  DELETED_AT: 'deleted_at' as simplifiedHeaderBiddersUiField,
  NAME: 'name' as simplifiedHeaderBiddersUiField,
  BIDDER: 'bidder' as simplifiedHeaderBiddersUiField,
  ACTIVE: 'active' as simplifiedHeaderBiddersUiField,
  SUPER_ONLY: 'super_only' as simplifiedHeaderBiddersUiField,
  PARAMS: 'params' as simplifiedHeaderBiddersUiField,
  PRIORITY: 'priority' as simplifiedHeaderBiddersUiField,
  UI_BIDDER_TYPES: 'ui_bidder_types' as simplifiedHeaderBiddersUiField,
  FLEXIBLE_BIDDING_ENABLED: 'flexible_bidding_enabled' as simplifiedHeaderBiddersUiField,
  FLEXIBLE_BIDDING_THRESHOLDS: 'flexible_bidding_thresholds' as simplifiedHeaderBiddersUiField,
  ENABLE_SPOTX_BIDDER_ASSOCIATION:
    'enable_spotx_bidder_association' as simplifiedHeaderBiddersUiField,
  ENABLE_MAGNITE_CTV_BIDDER_ASSOCIATION:
    'enable_magnite_ctv_bidder_association' as simplifiedHeaderBiddersUiField,
};

export interface IHeaderBidderParams {
  urlQueries: IHeaderBidderQueries;
  selectedChannelIds: number[];
  preloads?: simplifiedHeaderBiddersPreload[];
  uiFields?: simplifiedHeaderBiddersUiField[];
}

export interface IHeaderBidderListInfoWithCount {
  HeaderBidderListInfo: IHeaderBidderListInfo[];
  TotalCount: number;
}

export async function getHeaderbiddersWithPagination(
  publisherID: string,
  { urlQueries = {}, selectedChannelIds = [], preloads = [], uiFields = [] }: IHeaderBidderParams,
): Promise<[IHeaderBidderListInfoWithCount | undefined, string | undefined]> {
  let reqUrl = createUrl('/bidders', publisherID, 'v2');
  reqUrl = addUrlQueries(urlQueries, reqUrl);
  const reqBody = { selectedChannelIds, preloads, uiFields };

  try {
    return [
      (await restPost<IHeaderBidderListInfoWithCount>(reqUrl, transformReqBody(reqBody)))
        .parsedBody,
      undefined,
    ];
  } catch (e) {
    return [undefined, e];
  }
}

// Transforms passed in queries and appends them to requestURL
function addUrlQueries(urlQueries: IHeaderBidderQueries, reqUrl: string): string {
  for (const [key, value] of Object.entries(urlQueries)) {
    switch (key) {
      case 'activeBidderFilter':
        reqUrl += '&active=' + value;
        break;
      case 'selectedPriorities':
        reqUrl += '&priority=' + String(value);
        break;
      case 'page':
        reqUrl += '&page=' + String(value);
        break;
      case 'pageSize':
        reqUrl += '&page_size=' + String(value);
        break;
      case 'search':
        reqUrl += '&search=' + String(value);
        break;
      case 'idSearch':
        reqUrl += '&id_search=' + String(value);
        break;
      case 'orderBy':
        reqUrl += '&order_by=' + String(value);
        break;
      case 'orderDirection':
        reqUrl += '&order_direction=' + String(value);
        break;
      case 'bidderGroupIDs':
        reqUrl += '&bidder_group_ids=' + value;
        break;
      case 'demandSources':
        reqUrl += '&demand_sources=' + String(value);
        break;
      case 'bidderParamsSearch':
        reqUrl += '&bidder_params_search=' + String(value);
        break;
      case 'bidderLabels':
        reqUrl += '&bidder_labels=' + String(value);
        break;
    }
  }
  return reqUrl;
}

// determines if the search string only contains numbers and if so, returns a string with those numbers
export const createIDSearch = (search: string): string => {
  search = search.trim();
  if (onlyNumeric(search)) {
    return search;
  }
  return '';
};

// maps the bidder field name (from UI) to the db field name (mysql)
// add to this map for the paginated order by using list-table
export const bidderFieldToDBFieldMap: { [key: string]: string } = {
  Bidder: 'demand_source_ui_to_db_name.ui_name', // orders by joined table ui-name
  Active: 'active',
  Name: 'name',
  Priority: 'priority',
  PastWeekRevenue: 'past_week_revenue',
  SuperUserOnly: 'super_only',
  TargetedChannels: 'targeted_channels',
  TargetedChannelGroups: 'targeted_channel_groups',
};
